/**
 * Shared helper utilities for the frontend app.
 */

/**
 * Format a UNIX timestamp (seconds) into a human-readable local date/time string.
 * Falls back to ISO string if Intl formatting fails.
 */
export function formatTimestamp(
  timestamp?: number | null,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  },
  locale?: string
): string {
  if (timestamp === undefined || timestamp === null) {
    return "—";
  }

  const date = new Date(timestamp * 1000);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return date.toISOString();
  }
}

const ROLE_DISPLAY_MAP: Record<string, string> = {
  agency: "Agency",
  spaceship: "Spaceship",
};

function normalizeRole(role?: string | null): string {
  if (!role) return "";
  const key = role.toLowerCase();
  return ROLE_DISPLAY_MAP[key] ?? role.trim();
}

export function buildPlayerName(teamName?: string | null, role?: string | null): string {
  const safeTeam = (teamName ?? "").trim();
  const roleLabel = normalizeRole(role);
  if (!safeTeam) {
    return roleLabel || "";
  }
  return roleLabel ? `${safeTeam} ${roleLabel}` : safeTeam;
}

export function buildPlayerCodeName(teamCodeName?: string | null, role?: string | null): string {
  const safeCodeName = (teamCodeName ?? "").trim();
  const roleLabel = normalizeRole(role);
  if (!safeCodeName) {
    return roleLabel || "";
  }
  return roleLabel ? `${safeCodeName} ${roleLabel}` : safeCodeName;
}

function normalizeTimestampValue(
  value: number | string | null | undefined
): number {
  if (value === null || value === undefined) {
    return 0;
  }

  if (typeof value === "number") {
    // If it's already in milliseconds, keep as-is; otherwise assume seconds
    return value > 1e12 ? value : value * 1000;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function sortByTimestampDescending<T>(
  items: T[],
  getTimestamp: (item: T) => number | string | null | undefined
): T[] {
  return [...items].sort(
    (a, b) =>
      normalizeTimestampValue(getTimestamp(b)) -
      normalizeTimestampValue(getTimestamp(a))
  );
}

